/**
 * Jetway JP-500 Printer - ESC/POS Command Generator
 * Supports: Conference receipts, Manual comandas, Delivery labels, Kitchen labels
 */

const ESC = 0x1B;
const GS = 0x1D;
const LF = 0x0A;

const CMD = {
  INIT: [ESC, 0x40],
  ALIGN_LEFT: [ESC, 0x61, 0x00],
  ALIGN_CENTER: [ESC, 0x61, 0x01],
  ALIGN_RIGHT: [ESC, 0x61, 0x02],
  BOLD_ON: [ESC, 0x45, 0x01],
  BOLD_OFF: [ESC, 0x45, 0x00],
  DOUBLE_HEIGHT: [ESC, 0x21, 0x10],
  DOUBLE_WIDTH: [ESC, 0x21, 0x20],
  DOUBLE_HW: [ESC, 0x21, 0x30],
  NORMAL_SIZE: [ESC, 0x21, 0x00],
  CUT_PAPER: [GS, 0x56, 0x00],
  PARTIAL_CUT: [GS, 0x56, 0x01],
  FEED_LINES: (n) => [ESC, 0x64, n],
  UNDERLINE_ON: [ESC, 0x2D, 0x01],
  UNDERLINE_OFF: [ESC, 0x2D, 0x00],
};

function textToBytes(text) {
  return Array.from(new TextEncoder().encode(text));
}

function buildLine(left, right, width = 48) {
  const spaces = width - left.length - right.length;
  return left + ' '.repeat(Math.max(1, spaces)) + right;
}

function separator(char = '-', width = 48) {
  return char.repeat(width);
}

// ─── CONFERENCE RECEIPT (existing) ─────────────────────────────
export function generateConferenceReceipt(table, orders, businessName = 'Kaven Local') {
  const bytes = [];
  const push = (...b) => bytes.push(...b.flat());
  const text = (t) => push(textToBytes(t));
  const line = () => push(LF);
  const newline = (t) => { text(t); line(); };

  push(CMD.INIT);
  push(CMD.ALIGN_CENTER);
  push(CMD.DOUBLE_HW);
  newline(businessName);
  push(CMD.NORMAL_SIZE);
  newline('CUPOM DE CONFERENCIA');
  newline(separator('='));
  line();
  push(CMD.ALIGN_LEFT);
  push(CMD.BOLD_ON);
  newline(`Mesa: ${table.number}`);
  push(CMD.BOLD_OFF);
  newline(`Data: ${new Date().toLocaleString('pt-BR')}`);
  if (table.openedBy) newline(`Operador: ${table.openedBy}`);
  newline(separator('-'));
  push(CMD.BOLD_ON);
  newline(buildLine('Item', 'Qtd   Valor'));
  push(CMD.BOLD_OFF);
  newline(separator('-'));

  let total = 0;
  for (const order of orders) {
    const itemTotal = order.quantity * order.unitPrice;
    total += itemTotal;
    newline(order.beverageName);
    newline(buildLine(`  ${String(order.quantity).padStart(3)} x ${order.unitPrice.toFixed(2)}`, itemTotal.toFixed(2).padStart(8)));
  }
  newline(separator('='));
  push(CMD.DOUBLE_HEIGHT, CMD.BOLD_ON, CMD.ALIGN_RIGHT);
  newline(`TOTAL: R$ ${total.toFixed(2)}`);
  push(CMD.NORMAL_SIZE, CMD.BOLD_OFF, CMD.ALIGN_CENTER);
  line();
  newline('*** CONFERENCIA ***');
  newline('Obrigado pela preferencia!');
  push(...CMD.FEED_LINES(4));
  push(CMD.PARTIAL_CUT);
  return new Uint8Array(bytes);
}

export function generateManualComanda(table, items, businessName = 'Kaven Local') {
  const bytes = [];
  const push = (...b) => bytes.push(...b.flat());
  const text = (t) => push(textToBytes(t));
  const line = () => push(LF);
  const newline = (t) => { text(t); line(); };

  push(CMD.INIT, CMD.ALIGN_CENTER, CMD.BOLD_ON, CMD.DOUBLE_HW);
  newline(`MESA ${table.number}`);
  push(CMD.NORMAL_SIZE, CMD.BOLD_OFF);
  newline(new Date().toLocaleString('pt-BR'));
  newline(separator('-'));
  push(CMD.ALIGN_LEFT, CMD.DOUBLE_HEIGHT);
  for (const item of items) newline(`${item.quantity}x ${item.name}`);
  push(CMD.NORMAL_SIZE);
  newline(separator('-'));
  push(...CMD.FEED_LINES(3));
  push(CMD.PARTIAL_CUT);
  return new Uint8Array(bytes);
}

// ─── DELIVERY LABEL (80mm) ─────────────────────────────────────
export function generateDeliveryLabel(order) {
  const bytes = [];
  const push = (...b) => bytes.push(...b.flat());
  const text = (t) => push(textToBytes(t));
  const line = () => push(LF);
  const newline = (t) => { text(t); line(); };

  push(CMD.INIT);

  // Header
  push(CMD.ALIGN_CENTER, CMD.BOLD_ON);
  newline('KAVEN DELIVERY');
  push(CMD.BOLD_OFF);
  newline(`Pedido #${order.id}`);
  newline(new Date(order.createdAt).toLocaleString('pt-BR'));
  newline(separator('='));

  // Customer name - BOLD + DOUBLE HEIGHT
  push(CMD.ALIGN_LEFT, CMD.BOLD_ON, CMD.DOUBLE_HEIGHT);
  newline(order.customerName || 'Cliente');
  push(CMD.NORMAL_SIZE, CMD.BOLD_OFF);
  newline(order.customerPhone || '');
  newline(separator('-'));

  // Items with observations
  push(CMD.BOLD_ON);
  newline('ITENS:');
  push(CMD.BOLD_OFF);
  if (order.items) {
    for (const item of order.items) {
      push(CMD.DOUBLE_HEIGHT);
      newline(`${item.quantity}x ${item.name}`);
      push(CMD.NORMAL_SIZE);
      if (item.notes) {
        push(CMD.BOLD_ON, CMD.UNDERLINE_ON);
        newline(`   OBS: ${item.notes}`);
        push(CMD.BOLD_OFF, CMD.UNDERLINE_OFF);
      }
      newline(buildLine('', `R$ ${(item.total || item.price * item.quantity).toFixed(2)}`));
    }
  }
  newline(separator('-'));

  // Address + Landmark
  push(CMD.BOLD_ON);
  newline('ENDERECO:');
  push(CMD.BOLD_OFF);
  newline(order.neighborhood || 'N/A');
  if (order.address) newline(order.address);
  if (order.landmark) {
    push(CMD.BOLD_ON);
    newline(`Ref: ${order.landmark}`);
    push(CMD.BOLD_OFF);
  }
  newline(separator('-'));

  // Total + Payment
  push(CMD.ALIGN_RIGHT);
  newline(buildLine('Subtotal:', `R$ ${(order.subtotal || 0).toFixed(2)}`));
  newline(buildLine('Entrega:', `R$ ${(order.deliveryFee || 0).toFixed(2)}`));
  if (order.loyaltyDiscount) {
    push(CMD.BOLD_ON);
    newline(buildLine('Fidelidade:', `-R$ ${order.loyaltyDiscount.toFixed(2)}`));
    push(CMD.BOLD_OFF);
  }
  push(CMD.DOUBLE_HW, CMD.BOLD_ON);
  newline(`TOTAL: R$ ${(order.total || 0).toFixed(2)}`);
  push(CMD.NORMAL_SIZE, CMD.BOLD_OFF);

  // Payment method
  push(CMD.ALIGN_LEFT);
  newline(separator('-'));
  push(CMD.BOLD_ON);
  newline(`Pagamento: ${order.paymentMethod || 'Pix'}`);
  push(CMD.BOLD_OFF);
  if (order.changeFor) {
    push(CMD.DOUBLE_HEIGHT, CMD.BOLD_ON);
    newline(`TROCO PARA: R$ ${order.changeFor.toFixed(2)}`);
    push(CMD.NORMAL_SIZE, CMD.BOLD_OFF);
  }

  push(...CMD.FEED_LINES(3));
  push(CMD.PARTIAL_CUT);
  return new Uint8Array(bytes);
}

// ─── KITCHEN LABEL (simplified for quentinha lid) ──────────────
export function generateKitchenLabel(order) {
  const bytes = [];
  const push = (...b) => bytes.push(...b.flat());
  const text = (t) => push(textToBytes(t));
  const line = () => push(LF);
  const newline = (t) => { text(t); line(); };

  push(CMD.INIT);

  // Big order number
  push(CMD.ALIGN_CENTER, CMD.DOUBLE_HW, CMD.BOLD_ON);
  newline(`#${order.id}`);
  push(CMD.NORMAL_SIZE, CMD.BOLD_OFF);
  newline(order.customerName || '');
  newline(separator('-'));

  // Items - large text
  push(CMD.ALIGN_LEFT, CMD.DOUBLE_HEIGHT, CMD.BOLD_ON);
  if (order.items) {
    for (const item of order.items) {
      newline(`${item.quantity}x ${item.name}`);
      if (item.notes) {
        push(CMD.NORMAL_SIZE);
        newline(`  >> ${item.notes}`);
        push(CMD.DOUBLE_HEIGHT);
      }
    }
  }
  push(CMD.NORMAL_SIZE, CMD.BOLD_OFF);

  push(...CMD.FEED_LINES(2));
  push(CMD.PARTIAL_CUT);
  return new Uint8Array(bytes);
}

// ─── TEXT PREVIEWS ─────────────────────────────────────────────
export function previewReceipt(table, orders, businessName = 'Kaven Local') {
  const lines = [];
  const w = 48;
  const center = (t) => ' '.repeat(Math.max(0, Math.floor((w - t.length) / 2))) + t;

  lines.push(center(businessName));
  lines.push(center('CUPOM DE CONFERENCIA'));
  lines.push('='.repeat(w));
  lines.push(`Mesa: ${table.number}`);
  lines.push(`Data: ${new Date().toLocaleString('pt-BR')}`);
  if (table.openedBy) lines.push(`Operador: ${table.openedBy}`);
  lines.push('-'.repeat(w));
  lines.push(buildLine('Item', 'Qtd   Valor'));
  lines.push('-'.repeat(w));
  let total = 0;
  for (const order of orders) {
    const itemTotal = order.quantity * order.unitPrice;
    total += itemTotal;
    lines.push(order.beverageName);
    lines.push(buildLine(`  ${String(order.quantity).padStart(3)} x ${order.unitPrice.toFixed(2)}`, itemTotal.toFixed(2).padStart(8)));
  }
  lines.push('='.repeat(w));
  lines.push(buildLine('TOTAL:', `R$ ${total.toFixed(2)}`));
  lines.push('');
  lines.push(center('*** CONFERENCIA ***'));
  lines.push(center('Obrigado pela preferencia!'));
  return lines.join('\n');
}

export function previewDeliveryLabel(order) {
  const w = 48;
  const lines = [];
  const center = (t) => ' '.repeat(Math.max(0, Math.floor((w - t.length) / 2))) + t;

  lines.push(center('KAVEN DELIVERY'));
  lines.push(center(`Pedido #${order.id}`));
  lines.push(center(new Date(order.createdAt || Date.now()).toLocaleString('pt-BR')));
  lines.push('='.repeat(w));
  lines.push(`[BOLD+2X] ${order.customerName || 'Cliente'}`);
  lines.push(order.customerPhone || '');
  lines.push('-'.repeat(w));
  lines.push('[BOLD] ITENS:');
  if (order.items) {
    for (const item of order.items) {
      lines.push(`[2X] ${item.quantity}x ${item.name}`);
      if (item.notes) lines.push(`   [BOLD+UND] OBS: ${item.notes}`);
      lines.push(buildLine('', `R$ ${(item.total || item.price * item.quantity).toFixed(2)}`));
    }
  }
  lines.push('-'.repeat(w));
  lines.push('[BOLD] ENDERECO:');
  lines.push(order.neighborhood || 'N/A');
  if (order.address) lines.push(order.address);
  if (order.landmark) lines.push(`[BOLD] Ref: ${order.landmark}`);
  lines.push('-'.repeat(w));
  lines.push(buildLine('Subtotal:', `R$ ${(order.subtotal || 0).toFixed(2)}`));
  lines.push(buildLine('Entrega:', `R$ ${(order.deliveryFee || 0).toFixed(2)}`));
  if (order.loyaltyDiscount) lines.push(buildLine('[BOLD] Fidelidade:', `-R$ ${order.loyaltyDiscount.toFixed(2)}`));
  lines.push(`[BOLD+2X] TOTAL: R$ ${(order.total || 0).toFixed(2)}`);
  lines.push('-'.repeat(w));
  lines.push(`[BOLD] Pagamento: ${order.paymentMethod || 'Pix'}`);
  if (order.changeFor) lines.push(`[BOLD+2X] TROCO PARA: R$ ${order.changeFor.toFixed(2)}`);
  return lines.join('\n');
}

export function previewKitchenLabel(order) {
  const w = 32;
  const lines = [];
  const center = (t) => ' '.repeat(Math.max(0, Math.floor((w - t.length) / 2))) + t;

  lines.push(center(`=== PEDIDO #${order.id} ===`));
  lines.push(center(order.customerName || ''));
  lines.push('-'.repeat(w));
  if (order.items) {
    for (const item of order.items) {
      lines.push(`${item.quantity}x ${item.name}`);
      if (item.notes) lines.push(`  >> ${item.notes}`);
    }
  }
  lines.push('-'.repeat(w));
  return lines.join('\n');
}

// ─── COMMAND TICKET (Balança, Fase 7) ──────────────────────────
// Cupom parcial: número da comanda em DOUBLE_HEIGHT grande, peso, valor parcial.
export function generateCommandTicket(command, lastEntry, businessName = 'Kaven Local') {
  const bytes = [];
  const push = (...b) => bytes.push(...b.flat());
  const text = (t) => push(textToBytes(t));
  const line = () => push(LF);
  const newline = (t) => { text(t); line(); };

  push(CMD.INIT);
  push(CMD.ALIGN_CENTER, CMD.BOLD_ON);
  newline(businessName.toUpperCase());
  push(CMD.BOLD_OFF);
  newline('CUPOM DE COMANDA');
  newline(separator('='));

  // Número da comanda em DOUBLE_HW (grande) para enxergar de longe
  push(CMD.DOUBLE_HW, CMD.BOLD_ON);
  newline(`COMANDA #${command.number}`);
  push(CMD.NORMAL_SIZE, CMD.BOLD_OFF);
  newline(new Date().toLocaleString('pt-BR'));
  newline(separator('-'));

  // Último registro de peso em DOUBLE_HEIGHT
  push(CMD.ALIGN_LEFT, CMD.DOUBLE_HEIGHT, CMD.BOLD_ON);
  newline(`PESO: ${lastEntry.weight.toFixed(3)} kg`);
  newline(`R$/kg: ${lastEntry.pricePerKg.toFixed(2)}`);
  push(CMD.NORMAL_SIZE, CMD.BOLD_OFF);
  newline(separator('-'));

  // Histórico resumido se houver mais de 1 pesagem
  if (command.weightEntries && command.weightEntries.length > 1) {
    push(CMD.BOLD_ON);
    newline('HISTORICO DE PESAGENS:');
    push(CMD.BOLD_OFF);
    for (const e of command.weightEntries) {
      newline(buildLine(`  ${e.weight.toFixed(3)}kg x ${e.pricePerKg.toFixed(2)}`, e.subtotal.toFixed(2).padStart(8)));
    }
    newline(separator('-'));
  }

  push(CMD.ALIGN_RIGHT);
  newline(buildLine('Buffet:', `R$ ${(command.buffetTotal || 0).toFixed(2)}`));
  if ((command.itemsTotal || 0) > 0) {
    newline(buildLine('Bebidas/Itens:', `R$ ${command.itemsTotal.toFixed(2)}`));
  }
  push(CMD.DOUBLE_HEIGHT, CMD.BOLD_ON);
  newline(`TOTAL PARCIAL: R$ ${(command.total || 0).toFixed(2)}`);
  push(CMD.NORMAL_SIZE, CMD.BOLD_OFF, CMD.ALIGN_CENTER);
  line();
  newline('*** NAO E DOCUMENTO FISCAL ***');

  push(...CMD.FEED_LINES(3));
  push(CMD.PARTIAL_CUT);
  return new Uint8Array(bytes);
}

export function previewCommandTicket(command, lastEntry, businessName = 'Kaven Local') {
  const w = 48;
  const lines = [];
  const center = (t) => ' '.repeat(Math.max(0, Math.floor((w - t.length) / 2))) + t;
  lines.push(center(businessName.toUpperCase()));
  lines.push(center('CUPOM DE COMANDA'));
  lines.push('='.repeat(w));
  lines.push(center(`[2X+BOLD] COMANDA #${command.number}`));
  lines.push(center(new Date().toLocaleString('pt-BR')));
  lines.push('-'.repeat(w));
  lines.push(`[2X+BOLD] PESO: ${lastEntry.weight.toFixed(3)} kg`);
  lines.push(`[2X+BOLD] R$/kg: ${lastEntry.pricePerKg.toFixed(2)}`);
  lines.push('-'.repeat(w));
  if (command.weightEntries?.length > 1) {
    lines.push('HISTORICO:');
    for (const e of command.weightEntries) {
      lines.push(buildLine(`  ${e.weight.toFixed(3)}kg x ${e.pricePerKg.toFixed(2)}`, e.subtotal.toFixed(2).padStart(8)));
    }
    lines.push('-'.repeat(w));
  }
  lines.push(buildLine('Buffet:', `R$ ${(command.buffetTotal || 0).toFixed(2)}`));
  if ((command.itemsTotal || 0) > 0) lines.push(buildLine('Bebidas:', `R$ ${command.itemsTotal.toFixed(2)}`));
  lines.push(`[2X+BOLD] TOTAL: R$ ${(command.total || 0).toFixed(2)}`);
  return lines.join('\n');
}

// ─── COMMAND RECEIPT FINAL (Caixa, Fase 7) ─────────────────────
export function generateCommandReceipt(command, businessName = 'Kaven Local') {
  const bytes = [];
  const push = (...b) => bytes.push(...b.flat());
  const text = (t) => push(textToBytes(t));
  const line = () => push(LF);
  const newline = (t) => { text(t); line(); };

  push(CMD.INIT, CMD.ALIGN_CENTER, CMD.BOLD_ON);
  newline(businessName.toUpperCase());
  push(CMD.BOLD_OFF);
  newline('EXTRATO FINAL');
  newline(separator('='));

  push(CMD.DOUBLE_HW, CMD.BOLD_ON);
  newline(`COMANDA #${command.number}`);
  push(CMD.NORMAL_SIZE, CMD.BOLD_OFF);
  newline(new Date().toLocaleString('pt-BR'));
  newline(separator('-'));

  push(CMD.ALIGN_LEFT);
  if (command.weightEntries?.length > 0) {
    push(CMD.BOLD_ON);
    newline('BUFFET (kg):');
    push(CMD.BOLD_OFF);
    for (const e of command.weightEntries) {
      newline(buildLine(`  ${e.weight.toFixed(3)}kg x ${e.pricePerKg.toFixed(2)}`, e.subtotal.toFixed(2).padStart(8)));
    }
    newline(buildLine('Subtotal Buffet:', `R$ ${(command.buffetTotal || 0).toFixed(2)}`));
    newline(separator('-'));
  }

  if (command.items?.length > 0) {
    push(CMD.BOLD_ON);
    newline('BEBIDAS / ITENS:');
    push(CMD.BOLD_OFF);
    for (const i of command.items) {
      newline(buildLine(`  ${i.quantity}x ${i.name}`, i.subtotal.toFixed(2).padStart(8)));
    }
    newline(buildLine('Subtotal Bebidas:', `R$ ${(command.itemsTotal || 0).toFixed(2)}`));
    newline(separator('-'));
  }

  push(CMD.ALIGN_RIGHT, CMD.DOUBLE_HEIGHT, CMD.BOLD_ON);
  newline(`TOTAL: R$ ${(command.total || 0).toFixed(2)}`);
  push(CMD.NORMAL_SIZE, CMD.BOLD_OFF);
  push(CMD.ALIGN_LEFT);
  newline(`Pagamento: ${command.paymentMethod || '—'}`);

  push(CMD.ALIGN_CENTER);
  line();
  newline('Obrigado pela preferencia!');
  push(...CMD.FEED_LINES(3));
  push(CMD.PARTIAL_CUT);
  return new Uint8Array(bytes);
}

export function previewCommandReceipt(command, businessName = 'Kaven Local') {
  const w = 48;
  const lines = [];
  const center = (t) => ' '.repeat(Math.max(0, Math.floor((w - t.length) / 2))) + t;
  lines.push(center(businessName.toUpperCase()));
  lines.push(center('EXTRATO FINAL'));
  lines.push('='.repeat(w));
  lines.push(center(`[2X+BOLD] COMANDA #${command.number}`));
  lines.push(center(new Date().toLocaleString('pt-BR')));
  lines.push('-'.repeat(w));
  if (command.weightEntries?.length > 0) {
    lines.push('BUFFET (kg):');
    for (const e of command.weightEntries) lines.push(buildLine(`  ${e.weight.toFixed(3)}kg x ${e.pricePerKg.toFixed(2)}`, e.subtotal.toFixed(2).padStart(8)));
    lines.push(buildLine('Subtotal Buffet:', `R$ ${(command.buffetTotal || 0).toFixed(2)}`));
    lines.push('-'.repeat(w));
  }
  if (command.items?.length > 0) {
    lines.push('BEBIDAS / ITENS:');
    for (const i of command.items) lines.push(buildLine(`  ${i.quantity}x ${i.name}`, i.subtotal.toFixed(2).padStart(8)));
    lines.push(buildLine('Subtotal Bebidas:', `R$ ${(command.itemsTotal || 0).toFixed(2)}`));
    lines.push('-'.repeat(w));
  }
  lines.push(`[2X+BOLD] TOTAL: R$ ${(command.total || 0).toFixed(2)}`);
  lines.push(`Pagamento: ${command.paymentMethod || '—'}`);
  return lines.join('\n');
}

// ─── PORT MANAGEMENT (Web Serial) ──────────────────────────────
//
// Fase 21 (bug fix Jetway): cache global de porta + fluxo correto de flush.
// Antes: cada impressão chamava requestPort() → prompt sempre aparecia,
//        e write() seguido de close() imediato cortava o buffer (nada saía).
// Agora:
//   1. getPorts() primeiro — se já tem porta autorizada, usa direto (sem prompt).
//   2. Mantém a porta ABERTA entre impressões (singleton).
//   3. Flush correto: aguarda writer drain + pequeno delay pra impressora processar.

let cachedPort = null; // porta Jetway aberta e reutilizável durante a sessão

export async function connectPrinter({ interactive = true } = {}) {
  if (!('serial' in navigator)) {
    throw new Error('Web Serial API nao suportada. Use Chrome/Edge.');
  }

  // Se já temos uma porta aberta e funcional em cache, reaproveita.
  if (cachedPort && cachedPort.readable && cachedPort.writable) {
    return cachedPort;
  }

  // Tenta portas já autorizadas antes de abrir o prompt.
  const granted = await navigator.serial.getPorts();
  let port = granted && granted.length > 0 ? granted[0] : null;

  // Se não tem porta autorizada e pode ser interativo, pede ao usuário.
  if (!port) {
    if (!interactive) {
      throw new Error('Nenhuma porta autorizada. Clique em "Conectar Impressora" primeiro.');
    }
    port = await navigator.serial.requestPort();
  }

  // Abre (se ainda não estiver aberta). `readable` null = fechada.
  if (!port.readable) {
    await port.open({ baudRate: 9600, dataBits: 8, stopBits: 1, parity: 'none' });
  }

  cachedPort = port;
  return port;
}

/**
 * Libera a porta em cache (útil para trocar de impressora ou resetar).
 */
export async function disconnectPrinter() {
  if (!cachedPort) return;
  try {
    if (cachedPort.readable) await cachedPort.close();
  } catch {}
  cachedPort = null;
}

export function hasConnectedPrinter() {
  return !!(cachedPort && cachedPort.writable);
}

/**
 * Fase 20 (P1) — Auto-print silencioso (usa cache ou getPorts sem prompt).
 * Retorna { ok, reason? }. Nunca lança — falha silenciosa.
 */
export async function tryAutoPrint(data) {
  if (typeof navigator === 'undefined' || !('serial' in navigator)) {
    return { ok: false, reason: 'no_web_serial' };
  }
  try {
    const port = await connectPrinter({ interactive: false }).catch(() => null);
    if (!port) return { ok: false, reason: 'no_authorized_port' };
    await printData(port, data);
    return { ok: true };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[AUTO-PRINT] silenciado:', err?.message || err);
    return { ok: false, reason: 'error', error: String(err?.message || err) };
  }
}

/**
 * Envia bytes e AGUARDA o buffer drenar antes de retornar.
 * Crítico pra Jetway USB: sem o delay/flush, port.close() logo após
 * cortava o cupom pela metade ou não imprimia nada.
 */
export async function printData(port, data) {
  if (!port || !port.writable) {
    throw new Error('Porta da impressora não está pronta');
  }
  const writer = port.writable.getWriter();
  try {
    await writer.ready;
    await writer.write(data);
    // Dá tempo da impressora térmica processar (~20ms por KB é seguro).
    // Como cupons têm ~200-500 bytes, 150ms é folgado e imperceptível pro operador.
    await new Promise((r) => setTimeout(r, 150));
  } finally {
    try { writer.releaseLock(); } catch {}
  }
}

